import { useCallback, useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "sonner";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Radio, Save, Plus, X, MapPin, Loader2, Award, TrendingUp, ArrowLeft, ArrowRight, ShieldCheck } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { SellerLicenseRoute } from "../components/SellerLicenseRoute";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;
const MATERIALS = ["Cement", "TMT Bars", "Structural Steel", "Binding Wire", "Plates & Sheets", "Aggregate", "RMC"];
const INDIAN_STATES = ["Maharashtra", "Karnataka", "Tamil Nadu", "Gujarat", "Rajasthan", "Delhi", "Uttar Pradesh", "Telangana", "Kerala", "West Bengal", "Andhra Pradesh", "Madhya Pradesh"];

export default function SupplierDashboard() {
  return (
    <SellerLicenseRoute>
      <SupplierDashboardInner/>
    </SellerLicenseRoute>
  );
}

function SupplierDashboardInner() {
  const { user, api } = useAuth();
  const [profile, setProfile] = useState(null);
  const [today, setToday] = useState(null);
  const [elite, setElite] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, t, e] = await Promise.all([
        api.get("/seller/profile"),
        api.get("/seller/daily-rates/today"),
        api.get("/seller/elite/status"),
      ]);
      setProfile(p.data); setToday(t.data); setElite(e.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Load failed"); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (user === null) return null;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen bg-[#050506] text-[#F5F5F0]">
      <Toaster theme="dark" richColors position="top-right" />
      <Header />
      <main className="max-w-6xl mx-auto px-6 md:px-12 pt-28 pb-24">
        <Link to="/account" className="inline-flex items-center gap-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82] hover:text-[#D8B23E] mb-6">
          <ArrowLeft size={12}/> Back to account
        </Link>

        <div className="flex items-start gap-4 border-b border-white/10 pb-6 mb-8">
          <div>
            <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Vinci Seller Command</div>
            <h1 className="font-serif text-3xl">Your daily bid desk.</h1>
            <p className="font-body text-sm text-[#8A8A82] mt-2 max-w-2xl">
              Update rates every morning, toggle "Open to Bid" and tune your commission to move up the RFQ queue.
            </p>
          </div>
        </div>

        {/* Quick nav — expose adjacent seller modules */}
        <div className="grid md:grid-cols-3 gap-3 mb-8">
          <Link to="/account/deals" data-testid="supplier-nav-deals"
                className="group flex items-center gap-3 border border-white/10 hover:border-[#D8B23E]/60 rounded-sm bg-[#0a0a0a] p-4">
            <span className="h-9 w-9 rounded-sm bg-[#D8B23E]/15 text-[#D8B23E] grid place-items-center"><ShieldCheck size={16}/></span>
            <div>
              <div className="font-serif text-base">Indirect Trade Desk</div>
              <div className="font-body text-[11px] text-[#8A8A82]">180-day protection · NCND auto-signed</div>
            </div>
          </Link>
          <Link to="/league" data-testid="supplier-nav-league"
                className="group flex items-center gap-3 border border-white/10 hover:border-[#D8B23E]/60 rounded-sm bg-[#0a0a0a] p-4">
            <span className="h-9 w-9 rounded-sm bg-[#D8B23E]/15 text-[#D8B23E] grid place-items-center"><Award size={16}/></span>
            <div>
              <div className="font-serif text-base">Big-Cap League</div>
              <div className="font-body text-[11px] text-[#8A8A82]">Flagship infra allocations · elite induction</div>
            </div>
          </Link>
          <Link to="/supplier/license" data-testid="supplier-nav-license"
                className="group flex items-center gap-3 border border-white/10 hover:border-[#D8B23E]/60 rounded-sm bg-[#0a0a0a] p-4">
            <span className="h-9 w-9 rounded-sm bg-[#B87333]/15 text-[#B87333] grid place-items-center"><TrendingUp size={16}/></span>
            <div>
              <div className="font-serif text-base">License & renewal</div>
              <div className="font-body text-[11px] text-[#8A8A82]">Annual seller license · GST receipt</div>
            </div>
          </Link>
        </div>

        {loading ? (
          <div className="text-center font-body text-sm text-[#8A8A82] py-8">
            <Loader2 size={16} className="inline animate-spin mr-2"/> Loading command deck…
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            <DailyPulse api={api} today={today} onSaved={load}/>
            <div className="space-y-6">
              <CommissionSlider api={api} profile={profile} onSaved={load}/>
              <EliteCard elite={elite} api={api}/>
            </div>
            <ProfilePanel api={api} profile={profile} onSaved={load}/>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}

// ------------------- DAILY PULSE -------------------
function DailyPulse({ api, today, onSaved }) {
  const [items, setItems] = useState(() => today?.items?.length ? today.items : [{ material: "Cement", grade: "OPC 53", price_inr: "", moq: 0, notes: "" }]);
  const [openBid, setOpenBid] = useState(today?.open_to_bid !== false);
  const [busy, setBusy] = useState(false);

  const addRow = () => setItems(p => [...p, { material: MATERIALS[0], grade: "", price_inr: "", moq: 0, notes: "" }]);
  const removeRow = (i) => setItems(p => p.filter((_, idx) => idx !== i));
  const setField = (i, k, v) => setItems(p => p.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  const save = async () => {
    if (items.some(i => !i.material || !i.price_inr || Number(i.price_inr) <= 0)) {
      toast.error("Every row needs a material + positive price"); return;
    }
    setBusy(true);
    try {
      await api.post("/seller/daily-rates", {
        items: items.map(i => ({ ...i, price_inr: Number(i.price_inr), moq: Number(i.moq || 0) })),
        open_to_bid: openBid,
      });
      toast.success(openBid ? "Rates live · you're OPEN TO BID" : "Rates saved · bidding OFF");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="lg:col-span-2 border border-white/10 rounded-sm bg-[#0a0a0a] p-6" data-testid="daily-pulse-card">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Daily Pulse · 30-second rate update</div>
          <div className="font-serif text-xl mt-1">Post today's prices</div>
        </div>
        <ToggleBid open={openBid} onToggle={() => setOpenBid(o => !o)}/>
      </div>

      <div className="space-y-2">
        {items.map((r, i) => (
          <div key={i} data-testid={`rate-row-${i}`} className="grid grid-cols-12 gap-2 items-center border border-white/10 rounded-sm p-2 bg-black/20">
            <select data-testid={`rate-material-${i}`} value={r.material} onChange={e => setField(i, "material", e.target.value)}
                    className="col-span-3 bg-black/40 border border-white/15 rounded-sm px-2 py-1.5 text-sm">
              {MATERIALS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            <input data-testid={`rate-grade-${i}`} value={r.grade || ""} onChange={e => setField(i, "grade", e.target.value)}
                   placeholder="Grade / SKU"
                   className="col-span-2 bg-black/40 border border-white/15 rounded-sm px-2 py-1.5 text-sm"/>
            <input data-testid={`rate-price-${i}`} type="number" value={r.price_inr} onChange={e => setField(i, "price_inr", e.target.value)}
                   placeholder="Price ₹"
                   className="col-span-2 bg-black/40 border border-white/15 rounded-sm px-2 py-1.5 text-sm"/>
            <input data-testid={`rate-moq-${i}`} type="number" value={r.moq || ""} onChange={e => setField(i, "moq", e.target.value)}
                   placeholder="MOQ"
                   className="col-span-2 bg-black/40 border border-white/15 rounded-sm px-2 py-1.5 text-sm"/>
            <input data-testid={`rate-notes-${i}`} value={r.notes || ""} onChange={e => setField(i, "notes", e.target.value)}
                   placeholder="Notes"
                   className="col-span-2 bg-black/40 border border-white/15 rounded-sm px-2 py-1.5 text-sm"/>
            <button onClick={() => removeRow(i)} className="col-span-1 text-[#8A8A82] hover:text-[#E5484D] flex justify-center">
              <X size={14}/>
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between mt-4">
        <button onClick={addRow} data-testid="rate-add-row"
                className="inline-flex items-center gap-1.5 border border-white/15 hover:border-[#D8B23E] text-[#8A8A82] hover:text-[#F5F5F0] rounded-sm px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em]">
          <Plus size={11}/> Add another material
        </button>
        <button onClick={save} disabled={busy} data-testid="rate-save"
                className="inline-flex items-center gap-2 bg-[#D8B23E] hover:bg-[#c5a233] text-black rounded-sm px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.24em]">
          {busy ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Save pulse
        </button>
      </div>
    </div>
  );
}

function ToggleBid({ open, onToggle }) {
  return (
    <motion.button data-testid="toggle-open-to-bid" onClick={onToggle}
      whileTap={{ scale: 0.97 }}
      className={`relative inline-flex items-center gap-2 rounded-full px-5 py-2.5 font-grotesk text-[11px] uppercase tracking-[0.24em] transition-colors ${
        open ? "bg-[#3DBB84] text-black shadow-[0_0_36px_rgba(61,187,132,0.55)]"
             : "bg-[#141414] text-[#8A8A82] border border-white/15"
      }`}>
      <Radio size={12} className={open ? "animate-pulse" : ""}/>
      {open ? "OPEN TO BID · Today" : "Closed for bidding"}
    </motion.button>
  );
}

// ------------------- COMMISSION SLIDER -------------------
function CommissionSlider({ api, profile, onSaved }) {
  const [pct, setPct] = useState(profile?.commission_offer_pct || 1.0);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/seller/commission-offer", { commission_offer_pct: Number(pct) });
      toast.success(`Commission set to ${pct}% · applies to new RFQs`);
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-6" data-testid="commission-slider-card">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp size={14} className="text-[#D8B23E]"/>
        <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Commission offer</div>
      </div>
      <div className="font-serif text-xl">Move up the RFQ queue</div>
      <p className="font-body text-[11px] text-[#8A8A82] mt-1">
        Higher commission = higher priority in admin lead distribution. Applied only to new RFQs after save.
      </p>

      <div className="mt-4 flex items-baseline gap-3">
        <div className="font-serif text-4xl text-[#D8B23E]">{Number(pct).toFixed(1)}%</div>
        <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#8A8A82]">to Vinci Trade Network</div>
      </div>

      <input data-testid="commission-slider" type="range" min="0.5" max="5" step="0.1" value={pct}
             onChange={e => setPct(e.target.value)}
             className="w-full mt-3 accent-[#D8B23E]"/>
      <div className="flex justify-between font-grotesk text-[9px] uppercase tracking-[0.22em] text-[#8A8A82] mt-1">
        <span>0.5% · Standard</span><span>5% · Priority</span>
      </div>

      <button onClick={save} disabled={busy} data-testid="commission-save"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#D8B23E] hover:bg-[#c5a233] text-black rounded-sm px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.24em]">
        {busy ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Save commission
      </button>
    </div>
  );
}

// ------------------- ELITE CARD -------------------
function EliteCard({ elite, api }) {
  const status = elite?.status || "not_applied";
  const download = async () => {
    try {
      const r = await api.get("/seller/elite/certificate/download", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url;
      a.download = "vinci-elite-certificate.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Certificate not ready"); }
  };
  return (
    <div className="border border-[#D8B23E]/40 bg-gradient-to-br from-[#D8B23E]/[0.06] to-[#B87333]/[0.02] rounded-sm p-6" data-testid="elite-card">
      <div className="flex items-center gap-2 mb-1">
        <Award size={14} className="text-[#D8B23E]"/>
        <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E]">Elite Seller · Grand Slam</div>
      </div>
      <div className="font-serif text-xl">The top of the RFQ stack.</div>
      <p className="font-body text-[11px] text-[#8A8A82] mt-1">
        Elite scrutiny opens priority access to Vinci's largest deals and site visits.
      </p>
      <div className="mt-4">
        {status === "elite" && (
          <>
            <div className="inline-flex items-center gap-1.5 rounded-full border border-[#D8B23E]/60 text-[#D8B23E] px-3 py-1 font-grotesk text-[9px] uppercase tracking-[0.22em] mb-3">
              <ShieldCheck size={11}/> Elite Member
            </div>
            <div className="font-body text-[11px] text-[#8A8A82] mb-3">
              Certificate: <span className="font-mono text-[#F5F5F0]">{elite.certificate_no}</span>
            </div>
            <button onClick={download} data-testid="elite-download"
                    className="w-full inline-flex items-center justify-center gap-2 bg-[#D8B23E] hover:bg-[#c5a233] text-black rounded-sm px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.24em]">
              Download certificate
            </button>
          </>
        )}
        {status === "awaiting_scrutiny" && (
          <div className="inline-flex items-center gap-1.5 rounded-full border border-[#B87333]/60 text-[#B87333] px-3 py-1 font-grotesk text-[9px] uppercase tracking-[0.22em]" data-testid="elite-awaiting">
            <Loader2 size={11} className="animate-spin"/> Awaiting scrutiny · 5 business days
          </div>
        )}
        {status === "rejected" && (
          <div className="border border-[#E5484D]/40 bg-[#E5484D]/[0.05] rounded-sm p-3 mb-3" data-testid="elite-rejected">
            <div className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#E5484D]">Rejected</div>
            <div className="font-body text-[11px] text-[#8A8A82] mt-1 italic">{elite.rejection_reason || "Did not meet scrutiny criteria."}</div>
          </div>
        )}
        {status === "not_applied" && (
          <Link to="/supplier/elite/apply" data-testid="elite-apply-link"
                className="w-full inline-flex items-center justify-center gap-2 rounded-sm border border-[#D8B23E] text-[#D8B23E] hover:bg-[#D8B23E]/10 px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.24em]">
            Upgrade to Elite <ArrowRight size={11}/>
          </Link>
        )}
      </div>
    </div>
  );
}

// ------------------- PROFILE PANEL -------------------
function ProfilePanel({ api, profile, onSaved }) {
  const [limit, setLimit] = useState(profile?.daily_quotation_limit || 20);
  const [territories, setTerritories] = useState(profile?.geo_territories?.length ? profile.geo_territories : [{ state: "Maharashtra", cities: [] }]);
  const [busy, setBusy] = useState(false);

  const addTerritory = () => setTerritories(p => [...p, { state: "Karnataka", cities: [] }]);
  const removeTerritory = (i) => setTerritories(p => p.filter((_, idx) => idx !== i));
  const setState = (i, v) => setTerritories(p => p.map((t, idx) => idx === i ? { ...t, state: v } : t));
  const setCities = (i, csv) => setTerritories(p => p.map((t, idx) => idx === i ? { ...t, cities: csv.split(",").map(s => s.trim()).filter(Boolean) } : t));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/seller/profile", {
        daily_quotation_limit: Number(limit),
        geo_territories: territories,
      });
      toast.success("Profile updated");
      onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-white/10 rounded-sm bg-[#0a0a0a] p-6" data-testid="profile-panel">
      <div className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#D8B23E] mb-1">Operational profile</div>
      <div className="font-serif text-xl">Quota + territory map</div>

      <div className="mt-4">
        <div className="font-body text-[11px] text-[#8A8A82] mb-1">Daily quotation limit</div>
        <input data-testid="daily-quota" type="number" min="1" max="500" value={limit} onChange={e => setLimit(e.target.value)}
               className="w-full bg-black/40 border border-white/15 rounded-sm px-3 py-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#D8B23E]"/>
        <div className="font-body text-[10px] text-[#8A8A82] mt-1">Today: {profile?.daily_quote_count || 0} / {profile?.daily_quotation_limit || limit}</div>
      </div>

      <div className="mt-4">
        <div className="font-body text-[11px] text-[#8A8A82] mb-2">Geo territories</div>
        <div className="space-y-2">
          {territories.map((t, i) => (
            <div key={i} className="border border-white/10 rounded-sm bg-black/20 p-2" data-testid={`terr-row-${i}`}>
              <div className="flex items-center gap-2">
                <MapPin size={12} className="text-[#D8B23E]"/>
                <select data-testid={`terr-state-${i}`} value={t.state} onChange={e => setState(i, e.target.value)}
                        className="flex-1 bg-black/40 border border-white/15 rounded-sm px-2 py-1 text-sm">
                  {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <button onClick={() => removeTerritory(i)} className="text-[#8A8A82] hover:text-[#E5484D]"><X size={13}/></button>
              </div>
              <input data-testid={`terr-cities-${i}`} value={(t.cities || []).join(", ")}
                     onChange={e => setCities(i, e.target.value)}
                     placeholder="Cities (comma-separated)"
                     className="mt-2 w-full bg-black/40 border border-white/15 rounded-sm px-2 py-1 text-sm"/>
            </div>
          ))}
        </div>
        <button onClick={addTerritory} data-testid="terr-add"
                className="mt-2 inline-flex items-center gap-1.5 border border-white/15 hover:border-[#D8B23E] text-[#8A8A82] hover:text-[#F5F5F0] rounded-sm px-3 py-1.5 font-grotesk text-[10px] uppercase tracking-[0.22em]">
          <Plus size={11}/> Add state
        </button>
      </div>

      <button onClick={save} disabled={busy} data-testid="profile-save"
              className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[#D8B23E] hover:bg-[#c5a233] text-black rounded-sm px-4 py-2 font-grotesk text-[10px] uppercase tracking-[0.24em]">
        {busy ? <Loader2 size={12} className="animate-spin"/> : <Save size={12}/>} Save profile
      </button>
    </div>
  );
}
